import React, { useEffect, useState, useCallback, useRef } from 'react';
import TenantOnboard from './TenantOnboard';

const features = [
  {
    label: 'GST Billing',
    title: 'GST-ready invoices in seconds',
    desc: 'Create tax invoices with CGST/SGST or IGST automatically using state code. Print, download PDF, and share with customers.',
  },
  {
    label: 'Inventory',
    title: 'Live stock that updates with every sale',
    desc: 'Stock gets updated automatically for sales + returns. Track fast-moving items and avoid last-minute stockouts.',
  },
  {
    label: 'Customers',
    title: 'Customer dues & ledger made simple',
    desc: 'Save customers, track outstanding balance, view payment history, and follow up confidently—no notebooks or Excel.',
  },
  {
    label: 'Reports',
    title: 'Sales, profit & GST summaries',
    desc: 'Daily sales, top products, returns, and GST totals-easy to review and share with your accountant.',
  },
  {
    label: 'Support Chat',
    title: 'Ask in chat, get work done faster',
    desc: 'Use quick commands for sales and return reports, plus customer/product/user tasks-all from one support chat panel.',
  },
  {
    label: 'Barcode scanning',
    title: 'Use your device camera to scan products',
    desc: 'Built-in camera scanner for fast checkout — start billing instantly without an expensive barcode reader.',
  },
  {
    label: 'Returns & Credit Notes',
    title: 'Handle returns without confusion',
    desc: 'Create credit notes for returns, adjust stock automatically, and keep GST & reports accurate — no manual adjustments.',
  },
  {
    label: 'Multi-device & Cloud',
    title: 'Access your shop from anywhere',
    desc: 'Use Sellantra on mobile, tablet, or desktop. Your data is securely backed up in the cloud and always in sync.',
  },
  {
    label: 'Role-based Access',
    title: 'Control who can do what',
    desc: 'Owner, cashier, or staff — set permissions to keep data safe and prevent unwanted changes.',
  },
];

const automation = [
  { title: 'Auto stock updates', desc: 'Every invoice and return updates stock instantly—no manual adjustments.' },
  { title: 'Faster checkout', desc: 'Quick product search + clean billing flow built for rush-hour counters.' },
  { title: 'Branch-ready foundation', desc: 'Start with one shop, scale to multiple locations with clean records and audit-friendly data.' },
];

const pricingPlans = [
  {
    id: 'monthly',
    name: '1 Month',
    highlight: false,
    billed: 'Billed monthly',
    price: '₹100',
    sub: 'per month (incl. GST)',
    discount: '10% Limited time discount available',
    points: [
      'GST invoices + credit notes',
      'Inventory auto update (sales/returns)',
      'Customer ledger & dues',
      'Sales + GST reports',
    ],
  },
  {
    id: 'quarter',
    name: '3 Months',
    highlight: true,
    billed: 'Billed quarterly',
    price: '₹279',
    sub: 'per 3 months (incl. GST)',
    discount: 'Save 16% vs monthly',
    points: [
      'Everything in 1 Month',
      'Better value for growing shops',
      'Priority fixes & improvements',
      'Simple renewal anytime',
    ],
    badge: 'Popular',
  },
  {
    id: 'half',
    name: '6 Months',
    highlight: false,
    billed: 'Billed half-yearly',
    price: '₹549',
    sub: 'per 6 months (incl. GST)',
    discount: 'Save 18% vs monthly',
    points: [
      'Everything in 3 Months',
      'Lower effective monthly cost',
      'Stable billing & reporting flow',
      'Great for steady businesses',
    ],
  },
  {
    id: 'year',
    name: '12 Months',
    highlight: false,
    billed: 'Billed yearly',
    price: '₹1059',
    sub: 'per year (incl. GST)',
    discount: 'Save 20% vs monthly',
    points: [
      'Everything in 6 Months',
      'Best for long-term peace of mind',
      'Year-round updates',
      'Ideal for multi-season retail',
    ],
  },
];

export default function Home() {
  const playStoreUrl = 'https://play.google.com/store/apps/details?id=in.sellantra.app';
  const playStoreQrUrl = '/brand/android-app-qr.png';

  const [signedIn, setSignedIn] = useState(false);
  const [showOnboard, setShowOnboard] = useState(false);
  const [showContact, setShowContact] = useState(false);

  // ✅ fixed-header safe scroll support
  const navRef = useRef(null);

  useEffect(() => {
    try {
      setSignedIn(!!localStorage.getItem('token'));
    } catch {}
  }, []);

  // Force a clean light background (override global wallpaper)
  useEffect(() => {
    const cls = 'landing-mode';
    document.body.classList.add(cls);
    return () => document.body.classList.remove(cls);
  }, []);

  // ✅ Keep CSS var in sync with real header height (desktop/mobile)
  useEffect(() => {
    const updateNavH = () => {
      const h = navRef.current?.offsetHeight || 84;
      document.documentElement.style.setProperty('--nav-h', `${h}px`);
    };

    updateNavH();
    window.addEventListener('resize', updateNavH);

    // If fonts load later and change height, re-run once more shortly after mount
    const t = setTimeout(updateNavH, 250);

    return () => {
      window.removeEventListener('resize', updateNavH);
      clearTimeout(t);
    };
  }, []);

  // ✅ Bulletproof smooth scroll with offset so sections won't hide under fixed nav
const openOnboard = useCallback((e) => {
    e?.preventDefault?.();
    setShowOnboard(true);
  }, []);

  const closeOnboard = useCallback(() => setShowOnboard(false), []);

  return (
    <div className="landing-page">
      <div className="glow glow-1" aria-hidden="true" />
      <div className="glow glow-2" aria-hidden="true" />

      <header className="landing-nav" ref={navRef}>
        <div className="nav-inner">
          <div className="brand">
            <img
              className="brand-logo"
              src="/brand/sellantra-mark.png"
              alt="Sellantra logo"
              onError={(e) => {
                e.currentTarget.style.display = 'none';
              }}
            />
            <span className="brand-name">Sellantra</span>
          </div>

          <nav className="nav-links" aria-label="Primary">
            <a href="#features">Features</a>
            <a href="#automation">Workflows</a>
            <a href="#stories">Outcomes</a>
            <a href="#android">App</a>
            <a href="#pricing">Pricing</a>
          </nav>

          <div className="nav-actions">
            {signedIn ? (
              <a className="link" href="/dashboard">Dashboard</a>
            ) : (
              <a className="link" href="/login">Login</a>
            )}
            <a className="btn primary" href="/onboard" onClick={openOnboard}>Start free</a>
          </div>
        </div>
      </header>

      <section className="hero">
        <div className="hero-copy">
          <p className="eyebrow">Retail POS + GST Billing + Inventory — built for India</p>
          <h1>Billing that feels effortless. Stock that stays accurate.</h1>
          <p className="lead">
            Sellantra helps you bill faster, track inventory automatically, and keep customer dues clear —
            without spreadsheets or confusion.
          </p>
          <div className="cta-row">
            <a className="btn primary" href="/onboard" onClick={openOnboard}>Start free</a>
            {signedIn ? (
              <a className="btn ghost" href="/dashboard">Dashboard</a>
            ) : (
              <a className="btn ghost" href="/login">Login</a>
            )}
          </div>
          <div className="trust">
            <span className="pill">Built for Indian retail</span>
            <span>Made for kirana stores, wholesalers, and growing retailers who want clean billing + stock + reports.</span>
          </div>
        </div>

        <div className="hero-visual" aria-hidden="true">
          <div className="visual-card">
            <div className="visual-head">
              <div className="dot" />
              <div className="dot" />
              <div className="dot" />
              <span>Today at a glance</span>
            </div>
            <div className="visual-body">
              <div className="chart">
                <div className="bar b1" />
                <div className="bar b2" />
                <div className="bar b3" />
                <div className="bar b4" />
              </div>
              <div className="stat-block">
                <div>
                  <div className="stat-label">Today’s sales</div>
                  <div className="stat-value">₹ 38,420</div>
                </div>
                <div>
                  <div className="stat-label">Stock health</div>
                  <div className="stat-value good">On track</div>
                </div>
                <div>
                  <div className="stat-label">Pending dues</div>
                  <div className="stat-value warn">₹ 6,900</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section id="features" className="section">
        <div className="section-head">
          <p className="eyebrow">Why teams pick Sellantra</p>
          <h2>Everything you need for billing + stock + reports.</h2>
          <p className="section-lead">A simple system for daily sales, GST compliance, and inventory accuracy.</p>
        </div>
        <div className="feature-grid">
          {features.map((f) => (
            <div key={f.title} className="feature-card">
              <span className="tag">{f.label}</span>
              <h3>{f.title}</h3>
              <p>{f.desc}</p>
            </div>
          ))}
        </div>
      </section>

      <section id="automation" className="section split">
        <div className="section-copy">
          <p className="eyebrow">Workflows</p>
          <h2>Stop doing repeat work. Sellantra keeps things in sync.</h2>
          <p className="section-lead">
            Billing, stock, returns, and reports stay connected—so you spend less time fixing data and more time selling.
          </p>
          <div className="micro-cta">
            <a className="btn primary" href="https://youtu.be/_xMvzlmeYjU" target="_blank" rel="noreferrer">Watch on YouTube</a>
          </div>
        </div>
        <div className="automation-list">
          {automation.map((a) => (
            <div key={a.title} className="automation-card">
              <h4>{a.title}</h4>
              <p>{a.desc}</p>
            </div>
          ))}
        </div>
      </section>

      <section id="stories" className="section stories">
        <div className="section-head">
          <p className="eyebrow">Outcomes</p>
          <h2>Less confusion. More control at the counter.</h2>
        </div>
        <div className="story-grid">
          <div className="story-card">
            <div className="metric">2–3x</div>
            <div className="meta">Faster billing</div>
            <p className="desc">Quick product search + automatic totals keep checkout smooth even at peak time.</p>
          </div>
          <div className="story-card">
            <div className="metric">0 Excel</div>
            <div className="meta">Stock stays updated</div>
            <p className="desc">Sales and returns update inventory automatically so you always know what’s available.</p>
          </div>
          <div className="story-card">
            <div className="metric">Clear dues</div>
            <div className="meta">Better collections</div>
            <p className="desc">Customer balances and payment history help you follow up confidently and reduce pending amounts.</p>
          </div>
          <div className="story-card">
            <div className="metric">MFA on</div>
            <div className="meta">Safer sign-ins</div>
            <p className="desc">Multi-factor authentication adds an extra layer of protection for owner and staff accounts.</p>
          </div>
        </div>
      </section>

      <section id="android" className="section android-app">
        <div className="section-head">
          <p className="eyebrow">Android app</p>
          <h2>Sellantra on mobile.</h2>
          <p className="section-lead">
            Run billing, stock, and dues from your phone-fast, clean, and synced with your desktop.
          </p>
        </div>
        <div className="android-content">
          <div className="android-media" aria-hidden="true">
            <div className="android-visual is-hero">
              <img src="/screens/Screen1.png" alt="Sellantra mobile and tablet screens" />
            </div>
          </div>
          <div className="android-lower">
            <div className="android-card">
              <div className="android-points">
              <div className="android-point">
                <div className="point-title">Camera scan for fast billing</div>
                <div className="point-desc">Use your phone camera to add items instantly and speed up checkout.</div>
              </div>
              <div className="android-point">
                <div className="point-title">Live stock + dues sync</div>
                <div className="point-desc">Every sale, return, and payment updates your inventory and balances.</div>
              </div>
              <div className="android-point">
                <div className="point-title">Works seamlessly across devices</div>
                <div className="point-desc">Mobile and desktop stay in sync so your counter is always up to date.</div>
              </div>
                <div className="android-cta">
                  <a className="btn primary" href={playStoreUrl} target="_blank" rel="noreferrer">
                    Get the Android app
                  </a>
                </div>
              </div>
              <div className="qr-card qr-compact" aria-label="QR code placeholder">
                <img className="qr-img" src={playStoreQrUrl} alt="Scan to open Play Store" />
                <div className="qr-label">Scan to open Play Store</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section id="pricing" className="section pricing">
        <div className="pricing-card">
          <div className="pricing-head">
            <p className="eyebrow">Pricing</p>
            <h2>Simple plans. No setup cost.</h2>
            <p className="section-lead">
              Start free today. Upgrade anytime. (All plans include full features, updates, and support.)
            </p>
          </div>

          <div className="pricing-grid">
            {pricingPlans.map((p) => (
              <div key={p.id} className={`plan ${p.highlight ? 'featured' : ''}`}>
                <div className="plan-top">
                  <div className="plan-title-row">
                    <div className="plan-name">{p.name}</div>
                    {p.badge && <span className="badge">{p.badge}</span>}
                  </div>
                  <div className="plan-billed">{p.billed}</div>
                </div>

                <div className="plan-price">
                  <div className="plan-amount">{p.price}</div>
                  <div className="plan-sub">{p.sub}</div>
                  {p.discount && <div className="plan-discount">{p.discount}</div>}
                </div>

                <ul className="plan-points">
                  {p.points.map((pt) => <li key={pt}>{pt}</li>)}
                </ul>

                <div className="plan-cta">
                  <a className={`btn ${p.highlight ? 'primary' : 'ghost'}`} href="/onboard" onClick={openOnboard}>
                    Start free
                  </a>
                </div>

                {p.id === 'monthly' && (
                  <div className="plan-note">
                    Trial promo available: <strong>1 month free</strong> (limited time).
                  </div>
                )}
              </div>
            ))}
          </div>

          <div className="pricing-footnote">
            <div className="note-line">
              <span className="dotmini" /> Need multi-branch rollout or onboarding help?
            </div>
            <div className="note-line">
              <span className="dotmini" /> After your first month free, pick any paid plan at renewal.
              <button className="link-btn" type="button" onClick={() => setShowContact(true)}>Contact us</button>
            </div>
            {showContact && (
              <div className="contact-card">
                <div className="contact-row"><strong>Company:</strong> Anvibytes Systems</div>
                <div className="contact-row"><strong>Email:</strong> sujitsahu007@gmail.com</div>
                <div className="contact-row"><strong>Phone/WhatsApp:</strong> +91-9439214968</div>
                <div className="contact-row"><strong>Hours:</strong> Mon–Sat, 9am–6pm IST</div>
                <button className="link-btn" type="button" onClick={() => setShowContact(false)}>Close</button>
              </div>
            )}
          </div>
        </div>
      </section>

      <footer className="landing-footer">
        <div className="brand">Sellantra</div>
        <div className="footer-links">
          <button className="link-btn" type="button" onClick={() => setShowContact(true)}>Contact us</button>
          <a className="link-btn" href="/privacy">Privacy</a>
        </div>
        <div className="footer-note">Made for stores that want billing, inventory, and payments in one flow.</div>
      </footer>

      {showOnboard && (
        <div className="modal-host" role="dialog" aria-modal="true" aria-label="Signup">
          <div className="modal-backdrop" onClick={closeOnboard} />
          <div className="modal-panel" role="document">
            <header className="modal-head">
              <div>
                <div className="modal-eyebrow">Get started</div>
                <h3 className="modal-title">Register your shop / organisation</h3>
              </div>
              <button className="modal-close" onClick={closeOnboard} aria-label="Close">✕</button>
            </header>
            <div className="modal-body">
              <TenantOnboard embedded onDone={closeOnboard} onCancel={closeOnboard} />
            </div>
          </div>
        </div>
      )}

      <style>{`
        :root { --nav-h: 84px; }

        .landing-page {
          position: relative;
          overflow: visible;
          color: #0f172a;
          font-family: "Space Grotesk", "Manrope", "Inter", system-ui, sans-serif;
          min-height: 100vh;
          background: radial-gradient(140% 140% at 0% 0%, #f4f7ff 0%, #eef2ff 35%, #f9fbff 70%, #fefefe 100%);
          padding-top: var(--nav-h);
        }
        .landing-page .menu-fab { display: none !important; }

        .glow {
          position: absolute;
          filter: blur(80px);
          opacity: 0.65;
          pointer-events: none;
        }
        .glow-1 { width: 420px; height: 420px; background: #bcd6ff85; top: -80px; left: -120px; }
        .glow-2 { width: 520px; height: 520px; background: #ffe3f185; bottom: -160px; right: -120px; }

        html, body { scroll-padding-top: calc(var(--nav-h) + 16px); scroll-behavior: smooth; }

        .landing-nav {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          z-index: 20;
          background: #ffffff;
          border-bottom: 1px solid rgba(15,23,42,0.06);
          box-shadow: 0 8px 24px rgba(15,23,42,0.06);
        }
        .nav-inner {
          max-width: 1200px;
          margin: 0 auto;
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 18px;
          padding: 16px 28px;
        }
        .brand { display: inline-flex; align-items: center; gap: 10px; font-weight: 800; letter-spacing: -0.01em; }
        .brand-logo { width: 36px; height: 36px; border-radius: 12px; object-fit: contain; display: block; box-shadow: 0 8px 18px rgba(96,165,250,0.25); }
        .brand-name { color: #0f172a; font-size: 20px; font-weight: 800; }
        .nav-links { display: inline-flex; align-items: center; gap: 18px; color: #334155; font-weight: 600; }
        .nav-links a { color: inherit; text-decoration: none; opacity: 0.85; }
        .nav-links a:hover { opacity: 1; }
        .nav-actions { display: inline-flex; align-items: center; gap: 10px; }
        .link { color: #334155; text-decoration: none; font-weight: 600; }
        .nav-actions .link {
          border: 1px solid #e2e8f0;
          padding: 8px 12px;
          border-radius: 10px;
          background: #fff;
        }

        .hero {
          display: grid;
          grid-template-columns: 1.1fr 0.9fr;
          gap: 30px;
          padding: 72px 28px 48px;
          max-width: 1200px;
          margin: 0 auto;
          scroll-margin-top: calc(var(--nav-h) + 16px);
        }
        .hero-copy h1 { font-size: clamp(32px, 4vw, 54px); line-height: 1.1; margin: 12px 0; color: #0f172a; }
        .lead { max-width: 560px; margin: 0 0 18px; color: #475569; font-size: 17px; line-height: 1.6; }
        .eyebrow { text-transform: uppercase; letter-spacing: 0.12em; font-size: 12px; color: #4f46e5; margin: 0; }
        .cta-row { display: flex; align-items: center; gap: 12px; flex-wrap: wrap; }
        .btn {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          gap: 6px;
          padding: 12px 18px;
          border-radius: 12px;
          border: 1px solid transparent;
          font-weight: 700;
          text-decoration: none;
          cursor: pointer;
          transition: transform 120ms ease, box-shadow 120ms ease, opacity 120ms;
        }
        .btn:hover { transform: translateY(-1px); box-shadow: 0 10px 30px rgba(0,0,0,0.25); }
        .btn.primary { background: linear-gradient(135deg, #5d5fef, #60a5fa); color: #0c1226; border-color: #6ea8ff; }
        .btn.ghost { background: #fff; color: #0f172a; border-color: #e2e8f0; }
        .btn.text { background: transparent; color: #4f46e5; border-color: transparent; padding-left: 0; padding-right: 0; }

        .trust { display: inline-flex; align-items: center; gap: 10px; margin-top: 16px; color: #475569; font-size: 14px; flex-wrap: wrap; }
        .pill { padding: 6px 10px; border-radius: 999px; border: 1px solid #e2e8f0; background: #fff; font-weight: 700; }

        .hero-visual { display: flex; align-items: center; justify-content: center; }
        .visual-card {
          width: 100%;
          max-width: 460px;
          background: linear-gradient(160deg, rgba(255,255,255,0.9), rgba(255,255,255,0.75));
          border: 1px solid #e2e8f0;
          border-radius: 20px;
          box-shadow: 0 20px 60px rgba(15,23,42,0.1);
          overflow: hidden;
        }
        .visual-head {
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 12px 14px;
          background: rgba(248, 250, 252, 0.9);
          border-bottom: 1px solid #e2e8f0;
          color: #475569;
          font-weight: 700;
        }
        .visual-head .dot { width: 10px; height: 10px; border-radius: 50%; background: #5d5fef; box-shadow: 0 0 0 4px rgba(93,95,239,0.16); }
        .visual-body { padding: 18px; display: grid; gap: 16px; }
        .chart { display: flex; align-items: flex-end; gap: 10px; height: 180px; }
        .chart .bar { flex: 1; border-radius: 10px; background: linear-gradient(180deg, #6ea8ff, #203469); box-shadow: 0 8px 20px rgba(0,0,0,0.35); }
        .chart .b1 { height: 50%; }
        .chart .b2 { height: 76%; background: linear-gradient(180deg, #5d5fef, #182b58); }
        .chart .b3 { height: 100%; }
        .chart .b4 { height: 68%; background: linear-gradient(180deg, #9de2ff, #23406d); }
        .stat-block { display: grid; grid-template-columns: repeat(auto-fit, minmax(150px,1fr)); gap: 10px; }
        .stat-label { color: #64748b; font-size: 13px; }
        .stat-value { color: #0f172a; font-weight: 800; font-size: 16px; }
        .stat-value.good { color: #16a34a; }
        .stat-value.warn { color: #d97706; }

        .section { padding: 70px 28px; max-width: 1200px; margin: 0 auto; }
        .section { scroll-margin-top: calc(var(--nav-h) + 16px); }
        .section-head { text-align: left; max-width: 820px; }
        .section-head h2 { margin: 8px 0 6px; font-size: clamp(26px, 3vw, 40px); color: #0f172a; }
        .section-lead { margin: 0; color: #475569; }
        .feature-grid {
          margin-top: 26px;
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
          gap: 16px;
        }
        .feature-card {
          background: #fff;
          border: 1px solid #e2e8f0;
          border-radius: 16px;
          padding: 18px;
          box-shadow: 0 12px 32px rgba(15,23,42,0.08);
        }
        .feature-card h3 { margin: 8px 0; color: #0f172a; }
        .feature-card p { margin: 0 0 12px; color: #475569; line-height: 1.5; }
        .tag { display: inline-block; padding: 6px 10px; border-radius: 12px; background: #eef2ff; color: #4338ca; font-weight: 700; font-size: 12px; }

        .split {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 20px;
          align-items: center;
        }
        .automation-list { display: grid; gap: 12px; }
        .automation-card {
          background: #fff;
          border: 1px solid #e2e8f0;
          border-radius: 14px;
          padding: 16px;
          box-shadow: 0 10px 28px rgba(15,23,42,0.08);
        }
        .automation-card h4 { margin: 0 0 6px; color: #0f172a; }
        .automation-card p { margin: 0; color: #475569; }
        .micro-cta { display: flex; gap: 10px; flex-wrap: wrap; margin-top: 16px; }

        .stories .story-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
          gap: 14px;
          margin-top: 20px;
        }
        .story-card {
          padding: 16px;
          border-radius: 14px;
          background: #fff;
          border: 1px solid #e2e8f0;
          box-shadow: 0 10px 30px rgba(15,23,42,0.08);
        }
        .story-card .metric { font-size: 26px; font-weight: 800; color: #16a34a; }
        .story-card .meta { color: #334155; font-weight: 700; margin-bottom: 4px; }
        .story-card .desc { margin: 0; color: #475569; line-height: 1.4; }

        .android-app .section-head { max-width: 720px; }
        .android-content {
          margin-top: 18px;
          display: grid;
          gap: 12px;
        }
        .android-lower { max-width: 980px; }
        .android-card {
          background: #fff;
          border: 1px solid #e2e8f0;
          border-radius: 16px;
          padding: 12px;
          box-shadow: 0 12px 32px rgba(15,23,42,0.08);
          display: grid;
          grid-template-columns: 1fr 180px;
          gap: 10px;
          align-items: center;
        }
        .android-points {
          display: grid;
          gap: 10px;
        }
        .android-point { display: grid; gap: 4px; }
        .point-title { font-weight: 700; color: #1f2937; }
        .point-desc { color: #64748b; font-size: 13px; line-height: 1.4; }
        .android-cta {
          margin-top: 6px;
          display: flex;
          align-items: center;
          gap: 12px;
          flex-wrap: wrap;
        }
        .android-note { color: #64748b; font-size: 13px; }
        .android-media {
          display: grid;
          justify-items: start;
          max-width: 980px;
          margin-bottom: 6px;
        }
        .android-visual {
          background: #fff;
          border: 1px solid #e2e8f0;
          border-radius: 16px;
          padding: 12px;
          box-shadow: 0 12px 32px rgba(15,23,42,0.08);
          display: grid;
          place-items: center;
          width: 100%;
        }
        .android-visual.is-hero {
          padding: 0;
          border: 0;
          background: transparent;
          box-shadow: none;
          position: relative;
          margin-top: 0;
        }
        .android-visual img {
          width: 100%;
          max-width: 760px;
          height: auto;
          display: block;
        }
        .android-visual.is-hero img {
          transform: translateY(0);
          filter: drop-shadow(0 26px 44px rgba(15,23,42,0.24));
        }
        .qr-card {
          width: 100%;
          background: #f8fafc;
          border: 1px dashed #cbd5f5;
          border-radius: 14px;
          padding: 10px;
          display: grid;
          gap: 8px;
          justify-items: center;
          text-align: center;
        }
        .qr-img {
          width: 100%;
          aspect-ratio: 1 / 1;
          border-radius: 12px;
          border: 1px solid #dbe5ff;
          background: #fff;
          object-fit: contain;
          display: block;
        }
        .qr-label { color: #475569; font-size: 11px; line-height: 1.25; }

        .pricing { padding-bottom: 100px; }
        .pricing-card {
          background: linear-gradient(180deg, #fff, #f7f9ff);
          border: 1px solid #e2e8f0;
          border-radius: 18px;
          padding: 24px;
          box-shadow: 0 18px 48px rgba(15,23,42,0.12);
        }

        .pricing-grid {
          margin-top: 18px;
          display: grid;
          grid-template-columns: repeat(4, minmax(0, 1fr));
          gap: 14px;
          align-items: stretch;
        }

        .plan {
          background: #fff;
          border: 1px solid #e2e8f0;
          border-radius: 16px;
          padding: 16px;
          box-shadow: 0 12px 28px rgba(15,23,42,0.08);
          display: flex;
          flex-direction: column;
          gap: 12px;
        }
        .plan.featured {
          border-color: rgba(93,95,239,0.55);
          box-shadow: 0 18px 40px rgba(93,95,239,0.18);
          transform: translateY(-2px);
        }
        .plan-top { display: grid; gap: 6px; }
        .plan-title-row { display: flex; align-items: center; justify-content: space-between; gap: 10px; }
        .plan-name { font-weight: 900; color: #0f172a; letter-spacing: -0.01em; }
        .plan-billed { color: #64748b; font-size: 13px; }
        .badge {
          font-size: 12px;
          font-weight: 800;
          padding: 4px 10px;
          border-radius: 999px;
          background: #eef2ff;
          color: #4338ca;
          border: 1px solid #dbeafe;
          white-space: nowrap;
        }

        .plan-price { display: grid; gap: 2px; }
        .plan-amount { font-size: 30px; font-weight: 950; color: #0f172a; }
        .plan-sub { color: #475569; font-size: 13px; }
        .plan-discount { color: #16a34a; font-weight: 700; font-size: 12px; }

        .plan-points {
          margin: 0;
          padding-left: 18px;
          color: #475569;
          display: grid;
          gap: 6px;
          line-height: 1.35;
        }

        .plan-cta { display: flex; gap: 10px; flex-wrap: wrap; margin-top: 2px; }
        .plan-cta .btn { flex: 1; }
        .plan-note {
          margin-top: auto;
          font-size: 12px;
          color: #475569;
          background: #f8fafc;
          border: 1px dashed #e2e8f0;
          border-radius: 12px;
          padding: 10px;
        }

        .pricing-footnote {
          margin-top: 18px;
          padding-top: 14px;
          border-top: 1px solid rgba(15,23,42,0.08);
          color: #475569;
          font-size: 13px;
        }
        .note-line { display: flex; align-items: center; gap: 10px; }
        .dotmini { width: 8px; height: 8px; border-radius: 50%; background: #5d5fef; box-shadow: 0 0 0 4px rgba(93,95,239,0.12); }
        .note-line + .note-line { margin-top: 8px; }

        @media (max-width: 860px) {
          .android-card { grid-template-columns: 1fr; justify-items: start; }
          .android-media { justify-items: start; }
          .android-visual.is-hero img { transform: none; }
          .qr-card { justify-self: center; width: min(220px, 100%); }
        }
        .pricing-footnote .link-btn { margin-left: 8px; }
        .link-btn {
          background: none;
          border: none;
          color: #2563eb;
          cursor: pointer;
          font-weight: 700;
          padding: 0;
        }
        .link-btn:hover { text-decoration: underline; }
        .contact-card {
          margin-top: 10px;
          padding: 10px 12px;
          border: 1px solid #e2e8f0;
          border-radius: 12px;
          background: #f8fafc;
          color: #0f172a;
          display: grid;
          gap: 6px;
          max-width: 360px;
        }
        .contact-row { font-size: 13px; }

        .landing-footer {
          padding: 28px;
          border-top: 1px solid #e2e8f0;
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
          align-items: center;
          gap: 12px;
          color: #475569;
          background: #f5f7fb;
        }
        .landing-footer .brand { font-weight: 800; color: #0f172a; }
        .footer-links { display: inline-flex; gap: 12px; flex-wrap: wrap; }
        .footer-links a { color: #334155; text-decoration: none; }
        .footer-links a:hover { text-decoration: underline; }
        .footer-note { font-size: 13px; color: #475569; }

        .modal-host {
          position: fixed;
          inset: 0;
          z-index: 80;
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 24px 16px;
          box-sizing: border-box;
          overflow: auto;
        }
        .modal-backdrop { position: absolute; inset: 0; background: rgba(15,23,42,0.3); backdrop-filter: blur(6px); }
        .modal-panel {
          position: relative;
          width: min(960px, 96vw);
          max-height: 92vh;
          margin: auto;
          background: #ffffff;
          border: 1px solid #e2e8f0;
          border-radius: 16px;
          box-shadow: 0 24px 80px rgba(15,23,42,0.16);
          display: flex;
          flex-direction: column;
          overflow: hidden;
        }
        .modal-head { display: flex; justify-content: space-between; align-items: center; padding: 16px 18px; border-bottom: 1px solid #e2e8f0; }
        .modal-eyebrow { text-transform: uppercase; letter-spacing: 0.12em; font-size: 11px; color: #4f46e5; }
        .modal-title { margin: 4px 0 0; color: #0f172a; }
        .modal-close { background: #f8fafc; border: 1px solid #e2e8f0; color: #0f172a; border-radius: 10px; width: 36px; height: 36px; cursor: pointer; }
        .modal-close:hover { background: #e2e8f0; }
        .modal-body { padding: 12px 16px 20px; overflow: auto; }

        /* Override global wallpaper when on landing */
        .landing-mode {
          background: #f4f7ff !important;
          background-image: none !important;
          overflow-x: hidden;
          overflow-y: auto;
          -webkit-overflow-scrolling: touch;
        }
        .landing-mode #root {
          background: #f4f7ff;
          min-height: 100%;
          height: auto;
          overflow: visible;
        }
        .landing-mode #root .sk-shell,
        .landing-mode #root .sk-content {
          min-height: 100%;
          height: auto;
          overflow: visible;
        }
        .landing-mode #root .sk-content {
          padding: 0;
        }

        @media (max-width: 1100px) {
          .pricing-grid { grid-template-columns: repeat(2, minmax(0, 1fr)); }
        }

        @media (max-width: 1024px) {
          .landing-page { padding-top: 0; }
          .landing-nav { position: relative; flex-wrap: wrap; gap: 10px; }
          .nav-links { display: none; }
          .hero { grid-template-columns: 1fr; padding-top: 42px; }
          .visual-card { max-width: 100%; }
          .split { grid-template-columns: 1fr; }
        }

        @media (max-width: 640px) {
          .landing-nav, .hero, .section, .landing-footer { padding-left: 16px; padding-right: 16px; }
          .nav-inner { padding: 12px 16px; gap: 10px; flex-wrap: wrap; }
          .brand { width: 100%; justify-content: space-between; }
          .nav-links { display: none; }
          .nav-actions { width: 100%; justify-content: flex-start; gap: 10px; flex-wrap: wrap; }
          .hero-copy h1 { font-size: 32px; }
          .cta-row { width: 100%; }
          .btn { width: 100%; justify-content: center; }
          .nav-actions { flex-direction: column; }
          .pricing-grid { grid-template-columns: 1fr; }
        }
      `}</style>
    </div>
  );
}
